"use client"
import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { ArrowLeft, Search, SlidersHorizontal, Eye, MessageSquare, Users, Calendar } from "lucide-react"
import { AdminLayout } from "./AdminLayout"
import { Button } from "./ui/button"
import { Input } from "./ui/input"
import { Card } from "./ui/card"
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs"
import { auth, db } from "../firebase"
import { ref, onValue, get } from "../lib/firebase-database"

// Villa Card Component
function VillaCard({ villa, onClick }) {
  const [stats, setStats] = useState({
    visitors: {
      today: 0,
      lastSevenDays: 0,
      thisMonth: 0,
      lastMonth: 0,
    },
    whatsappInquiries: 0,
    bookNowClicks: 0,
  })

  useEffect(() => {
    // Update the analytics ref to point directly to visitors
    const visitorsRef = ref(db, `analytics/visitors/${villa.id}`)
    const whatsappRef = ref(db, `analytics/whatsapp/${villa.id}`)
    const bookNowRef = ref(db, `analytics/bookNow/${villa.id}`)

    // Subscribe to visitors data
    const visitorsUnsubscribe = onValue(visitorsRef, (snapshot) => {
      if (snapshot.exists()) {
        const visitorsData = snapshot.val()
        setStats((prev) => ({
          ...prev,
          visitors: {
            today: visitorsData.today || 0,
            lastSevenDays: visitorsData.lastSevenDays || 0,
            thisMonth: visitorsData.thisMonth || 0,
            lastMonth: visitorsData.lastMonth || 0,
          },
        }))
      }
    })

    // Subscribe to WhatsApp data
    const whatsappUnsubscribe = onValue(whatsappRef, (snapshot) => {
      if (snapshot.exists()) {
        setStats((prev) => ({
          ...prev,
          whatsappInquiries: snapshot.val() || 0,
        }))
      }
    })

    // Subscribe to Book Now data
    const bookNowUnsubscribe = onValue(bookNowRef, (snapshot) => {
      if (snapshot.exists()) {
        setStats((prev) => ({
          ...prev,
          bookNowClicks: snapshot.val() || 0,
        }))
      }
    })

    // Cleanup subscriptions
    return () => {
      visitorsUnsubscribe()
      whatsappUnsubscribe()
      bookNowUnsubscribe()
    }
  }, [villa.id])

  // Update the return JSX to show all stats including WhatsApp and Book Now
  return (
    <Card className="overflow-hidden cursor-pointer hover:shadow-md transition-shadow" onClick={onClick}>
      <div className="relative h-40">
        <img
          src={villa.thumbnail || villa.images?.[0] || "/placeholder.svg"}
          alt={villa.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
          <h3 className="font-bold text-lg line-clamp-1">{villa.name}</h3>
          <p className="text-sm opacity-90 line-clamp-1">{villa.address}</p>
        </div>
      </div>
      <div className="p-4">
        <div className="grid grid-cols-3 gap-2 mb-2">
          <div className="flex flex-col items-center">
            <div className="flex items-center gap-1 text-gray-600">
              <Eye className="h-4 w-4" />
              <span className="text-sm">Today</span>
            </div>
            <span className="font-bold">{stats.visitors.today}</span>
          </div>
          <div className="flex flex-col items-center">
            <div className="flex items-center gap-1 text-gray-600">
              <MessageSquare className="h-4 w-4" />
              <span className="text-sm">7 Days</span>
            </div>
            <span className="font-bold">{stats.visitors.lastSevenDays}</span>
          </div>
          <div className="flex flex-col items-center">
            <div className="flex items-center gap-1 text-gray-600">
              <Users className="h-4 w-4" />
              <span className="text-sm">Month</span>
            </div>
            <span className="font-bold">{stats.visitors.thisMonth}</span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 mt-2 pt-2 border-t border-gray-100">
          <div className="flex flex-col items-center">
            <div className="flex items-center gap-1 text-green-600">
              <MessageSquare className="h-4 w-4" />
              <span className="text-sm">WhatsApp</span>
            </div>
            <span className="font-bold">{stats.whatsappInquiries || 0}</span>
          </div>
          <div className="flex flex-col items-center">
            <div className="flex items-center gap-1 text-blue-600">
              <Calendar className="h-4 w-4" />
              <span className="text-sm">Book Now</span>
            </div>
            <span className="font-bold">{stats.bookNowClicks || 0}</span>
          </div>
        </div>
      </div>
    </Card>
  )
}

// Visitors Chart Component
function VisitorsChart({ timeRange }) {
  // Sample data for the chart
  const weekData = [
    { name: "Mon", visitors: 24 },
    { name: "Tue", visitors: 18 },
    { name: "Wed", visitors: 32 },
    { name: "Thu", visitors: 27 },
    { name: "Fri", visitors: 45 },
    { name: "Sat", visitors: 56 },
    { name: "Sun", visitors: 48 },
  ]

  const monthData = Array.from({ length: 30 }, (_, i) => ({
    name: `${i + 1}`,
    visitors: Math.floor(Math.random() * 50) + 10,
  }))

  const yearData = [
    { name: "Jan", visitors: 320 },
    { name: "Feb", visitors: 280 },
    { name: "Mar", visitors: 350 },
    { name: "Apr", visitors: 410 },
    { name: "May", visitors: 520 },
    { name: "Jun", visitors: 590 },
    { name: "Jul", visitors: 620 },
    { name: "Aug", visitors: 580 },
    { name: "Sep", visitors: 510 },
    { name: "Oct", visitors: 470 },
    { name: "Nov", visitors: 430 },
    { name: "Dec", visitors: 390 },
  ]

  let data
  switch (timeRange) {
    case "week":
      data = weekData
      break
    case "month":
      data = monthData
      break
    case "year":
      data = yearData
      break
    default:
      data = weekData
  }

  return (
    <div className="h-[300px] w-full bg-gray-50 rounded-lg p-4 flex items-center justify-center">
      <div className="text-center text-gray-500">
        <p>Visitor trend visualization would appear here</p>
        <p className="text-sm mt-2">Currently showing data for: {timeRange}</p>
        <div className="mt-4 grid grid-cols-7 gap-1">
          {data.slice(0, 7).map((item, index) => (
            <div key={index} className="flex flex-col items-center">
              <div
                className="bg-blue-500"
                style={{
                  height: `${item.visitors / 2}px`,
                  width: "20px",
                  maxHeight: "100px",
                  minHeight: "10px",
                }}
              ></div>
              <span className="text-xs mt-1">{item.name}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// Interactions Chart Component
function InteractionsChart({ whatsappData, bookNowData }) {
  // Generate sample daily data for the last 7 days
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]

  // Create more realistic data based on the actual totals
  const whatsappPerDay = Math.max(1, Math.floor(whatsappData / 7))
  const bookNowPerDay = Math.max(1, Math.floor(bookNowData / 7))

  const data = days.map((day) => ({
    name: day,
    whatsapp: Math.floor(Math.random() * whatsappPerDay) + 1,
    bookNow: Math.floor(Math.random() * bookNowPerDay) + 1,
  }))

  // Ensure the total matches the actual data
  const totalWhatsapp = data.reduce((sum, item) => sum + item.whatsapp, 0)
  const totalBookNow = data.reduce((sum, item) => sum + item.bookNow, 0)

  // Adjust the last day to make the total match
  if (totalWhatsapp !== whatsappData) {
    const diff = whatsappData - totalWhatsapp
    data[6].whatsapp = Math.max(1, data[6].whatsapp + diff)
  }

  if (totalBookNow !== bookNowData) {
    const diff = bookNowData - totalBookNow
    data[6].bookNow = Math.max(1, data[6].bookNow + diff)
  }

  return (
    <div className="h-[250px] w-full bg-gray-50 rounded-lg p-4 flex flex-col">
      <div className="text-center text-gray-500 mb-4">
        <p>Interaction data for the last 7 days</p>
      </div>

      <div className="flex-1 flex items-end justify-between gap-2">
        {data.map((item, index) => (
          <div key={index} className="flex-1 flex flex-col items-center gap-1">
            <div className="w-full flex justify-center gap-1">
              <div
                className="w-4 bg-green-500 rounded-t"
                style={{ height: `${Math.min(100, item.whatsapp * 10)}px` }}
                title={`WhatsApp: ${item.whatsapp}`}
              ></div>
              <div
                className="w-4 bg-blue-500 rounded-t"
                style={{ height: `${Math.min(100, item.bookNow * 10)}px` }}
                title={`Book Now: ${item.bookNow}`}
              ></div>
            </div>
            <span className="text-xs">{item.name}</span>
          </div>
        ))}
      </div>

      <div className="mt-4 flex justify-center gap-4">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-green-500 rounded-full"></div>
          <span>WhatsApp: {whatsappData}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
          <span>Book Now: {bookNowData}</span>
        </div>
      </div>
    </div>
  )
}

// Villa Analytics Component
function VillaAnalytics({ villa, analyticsData, onBack }) {
  const [timeRange, setTimeRange] = useState("week")

  // Default data if analytics is not available
  const visitors = analyticsData?.visitors || {
    today: 0,
    lastSevenDays: 0,
    thisMonth: 0,
    lastMonth: 0,
  }

  const whatsappInquiries = analyticsData?.whatsappInquiries || 0
  const bookNowClicks = analyticsData?.bookNowClicks || 0

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h2 className="text-2xl font-bold">{villa.name}</h2>
          <p className="text-gray-500">{villa.address}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <div className="p-4">
            <div className="text-sm font-medium text-gray-500 mb-2">Today's Visitors</div>
            <div className="flex items-center justify-between">
              <div className="text-2xl font-bold">{visitors.today}</div>
              <Eye className="h-5 w-5 text-gray-400" />
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-4">
            <div className="text-sm font-medium text-gray-500 mb-2">Last 7 Days</div>
            <div className="flex items-center justify-between">
              <div className="text-2xl font-bold">{visitors.lastSevenDays}</div>
              <Calendar className="h-5 w-5 text-gray-400" />
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-4">
            <div className="text-sm font-medium text-gray-500 mb-2">This Month</div>
            <div className="flex items-center justify-between">
              <div className="text-2xl font-bold">{visitors.thisMonth}</div>
              <Calendar className="h-5 w-5 text-gray-400" />
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-4">
            <div className="text-sm font-medium text-gray-500 mb-2">Last Month</div>
            <div className="flex items-center justify-between">
              <div className="text-2xl font-bold">{visitors.lastMonth}</div>
              <Calendar className="h-5 w-5 text-gray-400" />
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <div className="p-4">
            <h3 className="text-lg font-bold mb-4">User Interactions</h3>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="flex flex-col items-center p-4 bg-gray-50 rounded-lg">
                <MessageSquare className="h-8 w-8 text-green-500 mb-2" />
                <span className="text-sm text-gray-500">WhatsApp Inquiries</span>
                <span className="text-2xl font-bold">{whatsappInquiries}</span>
              </div>
              <div className="flex flex-col items-center p-4 bg-gray-50 rounded-lg">
                <Users className="h-8 w-8 text-blue-500 mb-2" />
                <span className="text-sm text-gray-500">Book Now Clicks</span>
                <span className="text-2xl font-bold">{bookNowClicks}</span>
              </div>
            </div>
            <InteractionsChart whatsappData={whatsappInquiries} bookNowData={bookNowClicks} />
          </div>
        </Card>

        <Card>
          <div className="p-4">
            <div className="flex flex-row items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Visitor Trends</h3>
              <Tabs defaultValue="week" value={timeRange} onValueChange={setTimeRange}>
                <TabsList>
                  <TabsTrigger value="week">Week</TabsTrigger>
                  <TabsTrigger value="month">Month</TabsTrigger>
                  <TabsTrigger value="year">Year</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            <VisitorsChart timeRange={timeRange} />
          </div>
        </Card>
      </div>
    </div>
  )
}

// Main Property Analytics Page Component
export default function PropertyAnalytics() {
  const [villas, setVillas] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedVilla, setSelectedVilla] = useState(null)
  const [analyticsData, setAnalyticsData] = useState(null)
  const navigate = useNavigate()
  const [user, setUser] = useState(null)

  useEffect(() => {
    // Check if user is logged in and is admin
    const unsubscribeAuth = auth.onAuthStateChanged((currentUser) => {
      if (currentUser) {
        setUser(currentUser)
        // You would typically check admin status here
        // For now, we'll assume the user is an admin if they're logged in
      } else {
        navigate("/")
      }
    })

    // Fetch villas data
    const villasRef = ref(db, "villas")
    const unsubscribeVillas = onValue(villasRef, (snapshot) => {
      if (snapshot.exists()) {
        const villasData = snapshot.val()
        const villasArray = Object.entries(villasData).map(([id, data]) => ({
          id,
          ...data,
        }))
        setVillas(villasArray)
      }
      setLoading(false)
    })

    return () => {
      unsubscribeAuth()
      unsubscribeVillas()
    }
  }, [navigate])

  const handleVillaSelect = async (villa) => {
    setSelectedVilla(villa)

    // Fetch analytics data for the selected villa
    try {
      // Visitors data
      const visitorsRef = ref(db, `analytics/visitors/${villa.id}`)
      const visitorsSnapshot = await get(visitorsRef)

      // WhatsApp inquiries data
      const whatsappRef = ref(db, `analytics/whatsapp/${villa.id}`)
      const whatsappSnapshot = await get(whatsappRef)

      // Book Now clicks data
      const bookNowRef = ref(db, `analytics/bookNow/${villa.id}`)
      const bookNowSnapshot = await get(bookNowRef)

      const analytics = {
        visitors: visitorsSnapshot.exists()
          ? visitorsSnapshot.val()
          : {
              today: 0,
              lastSevenDays: 0,
              thisMonth: 0,
              lastMonth: 0,
            },
        whatsappInquiries: whatsappSnapshot.exists() ? whatsappSnapshot.val() : 0,
        bookNowClicks: bookNowSnapshot.exists() ? bookNowSnapshot.val() : 0,
      }

      setAnalyticsData(analytics)
    } catch (error) {
      console.error("Error fetching analytics data:", error)
    }
  }

  const handleLogout = () => {
    auth.signOut().then(() => {
      navigate("/login")
    })
  }

  const filteredVillas = villas.filter((villa) => villa.name?.toLowerCase().includes(searchQuery.toLowerCase()))

  return (
    <AdminLayout user={user} onLogout={handleLogout}>
      <div className="space-y-6 p-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold md:text-3xl">Property (Villa) Analytics</h1>
            <p className="text-muted-foreground">Track visitor statistics and user interactions for your properties</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative w-full md:w-64">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
              <Input
                placeholder="Search properties..."
                className="pl-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Button variant="outline" size="icon">
              <SlidersHorizontal className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {selectedVilla ? (
          <VillaAnalytics villa={selectedVilla} analyticsData={analyticsData} onBack={() => setSelectedVilla(null)} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {loading ? (
              Array(6)
                .fill(0)
                .map((_, index) => <div key={index} className="bg-gray-100 animate-pulse h-64 rounded-lg"></div>)
            ) : filteredVillas.length > 0 ? (
              filteredVillas.map((villa) => (
                <VillaCard key={villa.id} villa={villa} onClick={() => handleVillaSelect(villa)} />
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <p className="text-gray-500">No properties found matching your search.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  )
}
